import { createPortal } from 'react-dom';
import React, { useState, useMemo, useRef, useEffect, useContext } from 'react';
import { InlineWidget } from "react-calendly";
import ProjectsListCard from '/src/Components/ProjectsListCard/ProjectsListCard';
import CleanProjectNames from '/src/services/CleanProjectNames';
import CTAButton from '/src/Components/CTAButton/CTAButton';
import { MoveRight } from 'lucide-react';
import { WindowSizeContext } from '/src/App';

import './style.css';

const ContentWrapper = ({ children, inline = false, handleOutsideClick = null }) => {
    if (inline) {
        return children;
    } else {
        const popupRef = useRef(null);

        useEffect(() => {
            const handleClick = (event) => {
                if (popupRef.current && !popupRef.current.contains(event.target)) {
                    handleOutsideClick?.();
                }
            };

            if (handleOutsideClick) {
                document.addEventListener('mousedown', handleClick);
                return () => document.removeEventListener('mousedown', handleClick);
            }
        }, [handleOutsideClick]);

        // Add effect to handle body scroll
        useEffect(() => {
            document.body.style.overflow = 'hidden';
            return () => {
                document.body.style.overflow = 'unset';
            };
        }, []);

        return createPortal(
            <div className="schedule-time-with-quoll-overlay">
                <div
                    className="schedule-time-with-quoll-popup"
                    ref={popupRef}
                    onClick={e => e.stopPropagation()}
                >
                    {children}
                </div>
            </div>,
            document.body
        );
    }
};

const ScheduleTimeWithQuoll = ({
    projects,
    propertyData,
    showTitle = true,
    inline = false,
    handleOutsideClick = null
}) => {
    const { deviceType } = useContext(WindowSizeContext);
    const [processStage, setProcessStage] = useState(0);
    const [projectsToDiscuss, setProjectsToDiscuss] = useState([]);
    const [alsoDiscussFullAnalysis, setAlsoDiscussFullAnalysis] = useState(false);
    // const [showAllProjects, setShowAllProjects] = useState(true);

    // Create unique house areas from projects
    const houseAreas = useMemo(() => {
        const uniqueAreas = new Map();

        // Add ALL option first
        uniqueAreas.set('all', {
            project_home_category_id: 'all',
            project_home_category: 'All Areas',
            house_category_icon: 'Home-Black.png' // You might want to adjust this icon
        });

        // Add other areas
        projects.forEach(project => {
            const id = project.project_home_category_id;
            if (!uniqueAreas.has(id)) {
                uniqueAreas.set(id, {
                    project_home_category_id: id,
                    project_home_category: project.project_home_category,
                    house_category_icon: project.house_category_icon
                });
            }
        });

        // Convert to array and sort alphabetically by category name, keeping 'All Areas' first
        return Array.from(uniqueAreas.values()).sort((a, b) => {
            if (a.project_home_category === 'All Areas') return -1;
            if (b.project_home_category === 'All Areas') return 1;
            return a.project_home_category.localeCompare(b.project_home_category);
        });
    }, [projects]);

    // Add state for selected filter
    const [selectedFilter, setSelectedFilter] = useState('all');

    // Filter projects based on selection
    const filteredProjects = useMemo(() => {
        if (selectedFilter === 'all') {
            return projects;
        }
        return projects.filter(project =>
            project.project_home_category_id === selectedFilter
        );
    }, [projects, selectedFilter]);


    // Get the address from property data and strip ", USA"
    const address = propertyData?.data?.property_details?.full_address.split(", USA")[0];

    const sectionHeader = "Schedule Time With Quoll";

    const handleProjectClick = (projectId) => {
        setProjectsToDiscuss(prev => {
            if (prev.includes(projectId)) {
                return prev.filter(id => id !== projectId);
            }
            return [...prev, projectId];
        });
    };

    // Create a handler for what happens when user adds a project --> We need to add the project to the projectsToDiscuss array
    const handleProjectAdd = (projectId) => {
        setProjectsToDiscuss(prev => [...prev, projectId]);
    };

    // Create a handler for what happens when user removes a project --> We need to remove the project from the projectsToDiscuss array
    const handleProjectRemove = (projectId) => {
        setProjectsToDiscuss(prev => prev.filter(id => id !== projectId));
    };

    console.log(projectsToDiscuss);
    // Create a function for formatting the projectsToDiscuss array into a string
    const formatProjectsToDiscuss = () => {
        // Take the ids in projectsToDiscuss and use them to get the names of the projects from the projects array
        const projectNames = projectsToDiscuss.map(id => {
            const project = projects.find(project => project.project_id === id);
            return CleanProjectNames(project.project_name);
        });

        // Return the project names as a string
        return projectNames.join(', ');
    }

    const stage0IntroSentence = "We're excited to schedule a time to discuss your project with you. Please select the projects you'd like to discuss with us and we'll get back to you with a time to schedule a call.";

    const renderStageZero = () => (
        <div className="schedule-stage-container">
            <p className="text-align-center" dangerouslySetInnerHTML={{ __html: stage0IntroSentence }} />
            <CTAButton text="Select Projects" onClick={() => setProcessStage(1)} />
        </div>
    );


    const renderStageOne = () => (
        <div className="schedule-stage-container">
            {showTitle &&
                <div className="schedule-stage-container-header">
                    {/* <h2>Which projects would you like to discuss with us?</h2> */}
                    {/* {projectsToDiscuss.length > 0 && (
                    <span>{projectsToDiscuss.length} Selected</span>
                )} */}
                </div>
            }
            <div className={`review-all-projects__filter-section ${deviceType}`}>
                <span className='review-all-projects__filter-section-label'>Filter by:</span>
                <div className={`review-all-projects__filter-bar ${deviceType}`}>
                    {houseAreas.map(area => (
                        <button
                            key={area.project_home_category_id}
                            className={`review-all-projects__filter-button ${selectedFilter === area.project_home_category_id ? 'active' : ''}`}
                            onClick={() => setSelectedFilter(area.project_home_category_id)}
                        >
                            {area.project_home_category}
                        </button>
                    ))}
                </div>
            </div>
            <div className="projects-grid send-schedule">
                {filteredProjects.map(project => {
                    const isProjectSelected = projectsToDiscuss.includes(project.project_id);
                    return (
                        <ProjectsListCard
                            key={project.project_id}
                            project={project}
                            handleProjectClick={isProjectSelected ? handleProjectRemove : handleProjectAdd}
                            isSelected={isProjectSelected}
                            showAllDetails={false}
                            simulatorView={true}
                            handleAddingProjectToSimulator={handleProjectAdd}
                            handleRemovingProjectFromSimulator={handleProjectRemove}
                        />
                    );
                })}
            </div>
            <CTAButton
                text="Add Topics to Discuss"
                onClick={projectsToDiscuss.length > 0 ? () => setProcessStage(2) : null}
                category={projectsToDiscuss.length > 0 ? "primary" : "disabled"}
            />
            {/* Next
            </button> */}
        </div>
    );

    const renderStageTwo = () => (
        <div className="schedule-stage-container">
            <h2>Would you like to also discuss our overall analysis of this property?</h2>
            <div className="schedule-radio-container">
                <label className="schedule-radio-label">
                    <input
                        type="radio"
                        name="discussAnalysis"
                        value="yes"
                        onChange={() => setAlsoDiscussFullAnalysis(true)}
                        checked={alsoDiscussFullAnalysis}
                    />
                    Yes
                </label>
                <label className="schedule-radio-label">
                    <input
                        type="radio"
                        name="discussAnalysis"
                        value="no"
                        onChange={() => setAlsoDiscussFullAnalysis(false)}
                        checked={!alsoDiscussFullAnalysis}
                    />
                    No
                </label>
            </div>
            <div className="schedule-buttons-container">
                <CTAButton text="Back" onClick={() => setProcessStage(1)} category="back" />
                {/* Back
                </button> */}
                <CTAButton text="Next" onClick={() => setProcessStage(3)} />
                {/* Next
                </button> */}
            </div>
        </div>
    );

    const renderStageThree = () => (
        <div className="schedule-stage-container">
            <div className="calendly-container">
                <InlineWidget
                    url="https://calendly.com/d/4gc-k42-dhh/15-minute-resiliency-strategy-session-with-quoll"
                    styles={{
                        height: '800px',
                        width: '100%',
                        borderRadius: '8px',
                        overflow: 'hidden',
                        boxShadow: 'var(--box-shadow-standard)',
                        backgroundColor: 'var(--page_background)',
                    }}
                    pageSettings={{
                        backgroundColor: 'var(--page_background)',
                        hideEventTypeDetails: false,
                        hideLandingPageDetails: false,
                        primaryColor: 'var(--page_background)',
                        secondaryColor: 'var(--hub_background)',
                        hideBranding: true
                    }}
                    prefill={{
                        email: 'ben@quollintelligence.com',
                        name: 'Ben Cohen',
                        customAnswers: {
                            a1: address,
                            a2: formatProjectsToDiscuss(),
                            a3: alsoDiscussFullAnalysis ? 1 : 2
                        }
                    }}
                />
            </div>
            <CTAButton text="Back" onClick={() => setProcessStage(2)} category="back" />
            {/* Back
            </button> */}
        </div>
    );


    const stagesArray = [
        {
            stage: 0,
            label: "About"
        },
        {
            stage: 1,
            label: "Select Projects"
        },
        {
            stage: 2,
            label: "Anything Else to Discuss?"
        },
        {
            stage: 3,
            label: "Schedule Time"
        }
    ]

    return (
        <ContentWrapper inline={inline} handleOutsideClick={handleOutsideClick}>
            <div className={`taking-action-section-container ${deviceType}`}>
                <h1>{sectionHeader}</h1>
                <div className={`send-project-to-others-stages-container ${deviceType}`}>
                    {stagesArray.map((stage, index) => {

                        let stageStatus = "inactive";
                        if (index === processStage) {
                            stageStatus = "active";
                        } else if (index < processStage) {
                            stageStatus = "complete";
                        }

                        return (
                            <div className={`send-project-to-others-stage ${deviceType}`} key={stage.stage}>
                                <div
                                    className={`send-project-to-others-stage-item ${stageStatus}`}
                                    onClick={index < processStage ? () => setProcessStage(index) : null}
                                >
                                    <span className="send-project-to-others-stage-label">{stage.label}</span>
                                </div>
                                {index < stagesArray.length - 1 && (
                                    <MoveRight className="send-project-to-others-stage-icon" />
                                )}
                            </div>
                        )
                    })}
                </div>
                {processStage === 0 && renderStageZero()}
                {processStage === 1 && renderStageOne()}
                {processStage === 2 && renderStageTwo()}
                {processStage === 3 && renderStageThree()}
            </div>
        </ContentWrapper>
    );
};

export default ScheduleTimeWithQuoll;